/* Discount amounts and eligibility always come from Shopify, never client-side arithmetic. */
(() => {
  if (customElements.get('taffstyle-cart-discount')) return;
  let busy = false;
  let cartRead = null;
  const widgets = () => [...document.querySelectorAll('taffstyle-cart-discount')];
  const normalize = code => String(code).trim().toLocaleUpperCase('en-US');
  const codesFrom = cart => Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
  const savingsFrom = cart => {
    const amounts = new Map();
    const add = (application, amount) => {
      if (!application?.title) return;
      const knownCode = codesFrom(cart).some(code => normalize(code.code) === normalize(application.title));
      if (application.type !== 'discount_code' && !(!application.type && knownCode)) return;
      const cents = Number(amount);
      if (!Number.isFinite(cents) || cents <= 0) return;
      const code = normalize(application.title);
      amounts.set(code, (amounts.get(code) || 0) + cents);
    };
    for (const application of cart.cart_level_discount_applications || []) add(application, application.total_allocated_amount);
    for (const item of cart.items || []) {
      for (const allocation of item.line_level_discount_allocations || []) add(allocation.discount_application, allocation.amount);
    }
    return amounts;
  };
  const popupCovered = (cart, root) => (cart.cart_level_discount_applications || []).some(application => {
    if (!(Number(application.total_allocated_amount) > 0)) return false;
    if (normalize(application.title) === 'COMEBACK10') return false;
    const tier = ['10 % Rabatt', '13 % Rabatt', '15 % Rabatt'].includes(application.title);
    return (tier && (!application.type || application.type === 'automatic')) ||
      (application.value_type === 'percentage' && application.target_selection === 'all' && Number(application.value) >= 10);
  }) || Number(root?.querySelector('[data-ts-tier-reward][data-preview="false"][data-applied-with-code="false"]')?.dataset.appliedRate || 0) >= 10;
  const roots = () => [...document.querySelectorAll('#cart, [data-ts-page-cart]')];
  async function json(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
      ...options, headers: { Accept: 'application/json', ...options.headers }
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error('Cart request failed');
    return result;
  }
  function readCart(cartUrl) {
    if (!cartRead) cartRead = json(cartUrl + '.js').finally(() => { cartRead = null; });
    return cartRead;
  }
  function disableControls() {
    const controls = roots().flatMap(root => [...root.querySelectorAll('button, input, select, textarea')]);
    const states = controls.map(control => [control, control.disabled]);
    controls.forEach(control => { control.disabled = true; });
    widgets().forEach(widget => widget.setAttribute('aria-busy', 'true'));
    return () => {
      states.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
      widgets().forEach(widget => widget.removeAttribute('aria-busy'));
    };
  }
  // Also block links and implicit checkout submissions while totals are being refreshed.
  for (const type of ['click', 'change', 'submit']) {
    document.addEventListener(type, event => {
      const root = event.target.closest?.('#cart, [data-ts-page-cart]');
      if (!root) return;
      const staleCheckout = root.dataset.tsDiscountStale === 'true' &&
        (type === 'submit' || event.target.closest('[name="checkout"]'));
      if ((busy && (type !== 'click' || event.target.closest('button, a, input, select'))) || staleCheckout) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }

  class TaffstyleCartDiscount extends HTMLElement {
    connectedCallback() {
      // Section HTML can connect this element before the parser has added its children.
      queueMicrotask(() => this.initialize());
    }

    captureLiquidCodes() {
      this.liquidCodes ??= [...this.querySelectorAll('[data-liquid-code]')].map(el => normalize(el.dataset.liquidCode));
    }

    initialize() {
      if (!this.isConnected || !this.querySelector('[data-discount-input]')) return;
      this.captureLiquidCodes();
      if (!this.initialized) {
        this.initialized = true;
        this.addEventListener('click', event => {
          const apply = event.target.closest('[data-discount-apply]');
          const remove = event.target.closest('[data-discount-remove]');
          if (!apply && !remove) return;
          event.preventDefault();
          event.stopPropagation();
          this.update(remove ? remove.dataset.discountRemove : null);
        });
        this.querySelector('[data-discount-input]').addEventListener('keydown', event => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          event.stopPropagation();
          this.update(null);
        });
      }
      if (!busy) readCart(this.dataset.cartUrl).then(cart => {
        if (this.isConnected && !busy) this.syncCodes(cart);
      }).catch(() => {}); // The Liquid-rendered codes remain usable if this optional read fails.
    }

    message(text, error = false, reload = false) {
      const message = this.querySelector('[data-discount-message]');
      message.textContent = text;
      message.hidden = !text;
      message.classList.toggle('is-error', error);
      this.querySelector('[data-discount-input]').setAttribute('aria-invalid', String(error));
      if (reload) {
        const link = document.createElement('a');
        link.href = window.location.href;
        link.textContent = this.dataset.reload;
        message.append(document.createTextNode(' '), link);
      }
    }

    syncCodes(cart) {
      this.captureLiquidCodes();
      const container = this.querySelector('[data-discount-codes]');
      const codes = codesFrom(cart).filter(code => !(normalize(code.code) === 'COMEBACK10' && code.applicable === false && popupCovered(cart, this.closest('#cart, [data-ts-page-cart]'))));
      const savings = savingsFrom(cart);
      const currency = typeof cart.currency === 'string' ? cart.currency : this.dataset.currency;
      let money = null;
      if (currency) {
        try { money = new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency }); } catch {}
      }
      if (!Array.isArray(cart.discount_codes)) return;
      container.replaceChildren();
      container.hidden = codes.length === 0;
      for (const discount of codes) {
        const pill = document.createElement('span');
        pill.className = 'ts-discount-code';
        const label = document.createElement('span');
        label.className = 'ts-discount-code-label';
        const title = document.createElement('strong');
        title.textContent = discount.code;
        const amount = discount.applicable === true ? savings.get(normalize(discount.code)) || 0 : 0;
        const status = document.createElement('small');
        status.textContent = discount.applicable === false ? this.dataset.notApplicable : amount > 0 ? this.dataset.appliedLabel : this.dataset.savedLabel;
        label.append(title, status);
        pill.append(label);
        if (amount > 0 && money) {
          const saving = document.createElement('strong');
          saving.className = 'ts-discount-saving';
          const formatted = money.format(amount / 100);
          saving.textContent = '−' + formatted;
          saving.setAttribute('aria-label', this.dataset.savingsLabel + ': ' + formatted);
          pill.append(saving);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.discountRemove = discount.code;
        remove.setAttribute('aria-label', this.dataset.removeLabel + ': ' + discount.code);
        remove.textContent = '×';
        pill.append(remove);
        container.append(pill);
      }
      // Shopify can accept shipping codes without exposing a shipping amount to Liquid.
      // In that case show checkout calculation, not a guessed shipping fee or grand total.
      const deferredShipping = codes.some(code => code.applicable === true && !this.liquidCodes.includes(normalize(code.code)) && !savings.has(normalize(code.code)));
      this.querySelector('[data-discount-checkout-note]').hidden = !deferredShipping;
      if (deferredShipping && cart.requires_shipping) {
        const root = this.closest('#cart, [data-ts-page-cart]');
        const delivery = root?.querySelector('.ts-cart-delivery > span:last-child, .ts-page-delivery > span:last-child');
        if (delivery) {
          delivery.textContent = this.dataset.calculated;
          delivery.classList.remove('ts-cart-free', 'ts-page-free');
        }
        const total = root?.querySelector('.ts-cart-total, .ts-page-total');
        if (total) total.hidden = true;
        const bottomTotal = root?.querySelector('[data-ts-bottom-total]');
        const bottomSubtotal = root?.querySelector('[data-ts-bottom-subtotal]');
        if (bottomTotal) bottomTotal.hidden = true;
        if (bottomSubtotal) bottomSubtotal.hidden = false;
        const shippingStatus = root?.querySelector('.ts-cart-shipping, .ts-page-shipping-status');
        if (shippingStatus) shippingStatus.hidden = true;
      }
    }

    async update(removeCode) {
      if (busy || !this.isConnected) return;
      const input = this.querySelector('[data-discount-input]');
      const value = input.value.trim();
      if (removeCode === null && !value) { this.message(this.dataset.empty, true); input.focus(); return; }
      if (removeCode === null && value.includes(',')) { this.message(this.dataset.invalid, true); return; }
      if (roots().some(root => root.getAttribute('aria-busy') === 'true' || root.querySelector('[name="checkout"]:disabled, input[name="updates[]"]:disabled, .ts-cart-items .processing, .ts-cart-items .removing'))) {
        this.message(this.dataset.busy);
        return;
      }
      const cartUrl = this.dataset.cartUrl;
      const instance = this.dataset.instance;
      const strings = { ...this.dataset };
      const drawer = document.getElementById('cart');
      const page = document.querySelector('[data-ts-page-cart]');
      const sectionIds = [...new Set([drawer && 'side-cart', page?.dataset.section].filter(Boolean))];
      const drawerNote = drawer?.querySelector('textarea[name="note"]')?.value;
      const drawerExpanded = drawer?.querySelector('.ts-cart-note')?.open;
      const scrollTop = drawer?.querySelector('.ts-cart-scroll')?.scrollTop || 0;
      const activeWidget = () => widgets().find(widget => widget.dataset.instance === instance);
      let posted = false;
      let coherent = true;
      busy = true;
      const restore = disableControls();
      this.message(strings.pending);
      const render = sections => {
        const parsed = {};
        for (const id of sectionIds) {
          if (typeof sections?.[id] !== 'string') throw new Error('Missing cart section');
          parsed[id] = new DOMParser().parseFromString(sections[id], 'text/html');
          const required = id === 'side-cart' ? '#shopify-section-side-cart [data-totalqty]' : '[data-ts-page-cart]';
          if (!parsed[id].querySelector(required)) throw new Error('Incomplete cart section');
        }
        if (drawer) {
          if (typeof window.ajaxCart?.load !== 'function') throw new Error('Cart not ready');
          window.ajaxCart.load({ sections }, drawer.classList.contains('toggle'));
          const note = drawer.querySelector('textarea[name="note"]');
          if (note && drawerNote !== undefined) note.value = drawerNote;
          if (drawerExpanded && drawer.querySelector('.ts-cart-note')) drawer.querySelector('.ts-cart-note').open = true;
          const scroll = drawer.querySelector('.ts-cart-scroll');
          if (scroll) scroll.scrollTop = scrollTop;
        }
        if (page) {
          if (typeof window.taffstyleCartPage?.render !== 'function') throw new Error('Cart page not ready');
          window.taffstyleCartPage.render(sections[page.dataset.section]);
        }
        roots().forEach(root => { delete root.dataset.tsDiscountStale; });
      };
      const refreshSections = () => {
        const url = new URL(cartUrl, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        return json(url);
      };
      try {
        const current = await readCart(cartUrl);
        if (!this.isConnected) return;
        // Keep every currently stored code, including codes temporarily ineligible for this cart.
        const existing = codesFrom(current).map(discount => discount.code);
        if (!Array.isArray(current.discount_codes)) {
          widgets().forEach(widget => existing.push(...widget.liquidCodes));
        }
        const unique = new Map(existing.map(code => [normalize(code), code]));
        if (removeCode !== null) unique.delete(normalize(removeCode));
        else unique.set(normalize(value), value);
        posted = true;
        coherent = false;
        const result = await json(cartUrl + '/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: [...unique.values()].join(','), sections: sectionIds, sections_url: cartUrl })
        });
        if (!Array.isArray(result.discount_codes)) throw new Error('Missing discount eligibility');
        let sections = result.sections;
        if (sectionIds.some(id => !sections?.[id])) sections = await refreshSections();
        render(sections);
        coherent = true;
        widgets().forEach(widget => widget.syncCodes(result));
        const next = activeWidget();
        const applied = codesFrom(result).find(discount => normalize(discount.code) === normalize(value));
        const removed = !codesFrom(result).some(discount => normalize(discount.code) === normalize(removeCode));
        const succeeded = removeCode !== null ? removed : applied?.applicable === true;
        if (next) {
          const superseded = removeCode === null && normalize(value) === 'COMEBACK10' && popupCovered(result, next.closest('#cart, [data-ts-page-cart]'));
          next.message(superseded ? strings.popupCovered : succeeded ? (removeCode !== null ? strings.removed : strings.success) : strings.invalid, !succeeded && !superseded);
          const nextInput = next.querySelector('[data-discount-input]');
          nextInput.value = succeeded || superseded ? '' : value;
          nextInput.focus({ preventScroll: true });
        }
      } catch (error) {
        // Never repeat a possibly committed POST; recover only by reading current cart state.
        if (posted && error.name !== 'TimeoutError' && error.name !== 'AbortError') {
          try {
            const [current, sections] = await Promise.all([readCart(cartUrl), refreshSections()]);
            render(sections);
            coherent = true;
            widgets().forEach(widget => widget.syncCodes(current));
          } catch { coherent = false; }
        }
        const next = activeWidget();
        if (next) {
          next.querySelector('[data-discount-input]').value = value;
          next.message(strings.error, true, !coherent);
        }
      } finally {
        busy = false;
        restore();
        if (!coherent) roots().forEach(root => {
          root.dataset.tsDiscountStale = 'true';
          root.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = true; });
        });
      }
    }
  }
  customElements.define('taffstyle-cart-discount', TaffstyleCartDiscount);
})();



